import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './ProductManagement.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductManagement = () => {
  const { sessionToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    loadBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      loadProducts();
    }
  }, [selectedBrand]);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API}/merchant/brands`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success) {
        setBrands(response.data.brands);
        if (response.data.brands.length > 0) {
          setSelectedBrand(response.data.brands[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(
        `${API}/merchant/products?brand_id=${selectedBrand}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleCreateProduct = async (formData) => {
    try {
      console.log('Creating product with data:', formData);
      
      const response = await axios.post(
        `${API}/merchant/products`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log('Product creation response:', response.data);
      
      if (response.data.success) {
        setShowProductModal(false);
        loadProducts();
        alert('✅ Product created successfully!');
      } else {
        alert('Failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating product:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create product';
      alert('❌ Error: ' + errorMsg);
    }
  };

  const handleDeactivateProduct = async (productId) => {
    if (!window.confirm('Deactivate this product?')) return;
    
    try {
      await axios.put(
        `${API}/merchant/products/${productId}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      loadProducts();
      alert('Product deactivated');
    } catch (error) {
      console.error('Error deactivating product:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('⚠️ Delete this product permanently? This cannot be undone!')) return;
    
    try {
      await axios.delete(
        `${API}/merchant/products/${productId}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      loadProducts();
      alert('✅ Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('❌ Failed to delete product');
    }
  };

  const handleCloneProduct = (product) => {
    // Clone product data but clear name and SKU
    setEditingProduct({
      ...product,
      product_name: '',  // Clear so merchant must rename
      product_id_custom: '',  // Clear so merchant must change SKU
      id: null  // Remove ID so it creates new product
    });
    setShowProductModal(true);
  };

  if (loading) {
    return <Layout><div className="loading">Loading...</div></Layout>;
  }

  if (brands.length === 0) {
    return (
      <Layout>
        <div className="product-management">
          <div className="empty-state-large">
            <h2>No Brands Yet</h2>
            <p>Create a brand first before adding products</p>
            <button className="btn-primary" onClick={() => window.location.href = '/merchant/brands'}>
              Create Brand
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="product-management">
        <div className="page-header">
          <div>
            <h1>📦 Product Management</h1>
            <select 
              className="brand-selector"
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
            >
              {brands.map(brand => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => setShowCSVModal(true)}>
              📊 Upload CSV
            </button>
            <button className="btn-primary" onClick={() => setShowProductModal(true)}>
              + Add Product
            </button>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Products Yet</h3>
            <p>Add products individually or upload a CSV file</p>
            <div style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
              <button className="btn-secondary" onClick={() => setShowCSVModal(true)}>
                Upload CSV
              </button>
              <button className="btn-primary" onClick={() => setShowProductModal(true)}>
                Add Product
              </button>
            </div>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className={`product-card ${!product.is_active ? 'inactive' : ''}`}>
                <img src={product.picture_link} alt={product.product_name} />
                <div className="product-info">
                  <h3>{product.product_name}</h3>
                  <p className="product-price">${product.price}</p>
                  <div className="product-meta">
                    <span>{product.size}</span>
                    <span>{product.gender}</span>
                    <span>{product.category}</span>
                  </div>
                  <div className="product-stats">
                    <span>👁 {product.views}</span>
                    <span>👗 {product.tryon_count}</span>
                    <span>📦 {product.stock_quantity}</span>
                  </div>
                  {!product.is_active && <span className="inactive-badge">Inactive</span>}
                  <div className="product-actions">
                    <button 
                      className="clone-btn"
                      onClick={() => handleCloneProduct(product)}
                    >
                      📋 Clone
                    </button>
                    <button 
                      className="deactivate-btn"
                      onClick={() => handleDeactivateProduct(product.id)}
                      disabled={!product.is_active}
                    >
                      {product.is_active ? 'Deactivate' : 'Deactivated'}
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showProductModal && (
          <ProductModal
            brandId={selectedBrand}
            initialData={editingProduct}
            onClose={() => {
              setShowProductModal(false);
              setEditingProduct(null);
            }}
            onSave={handleCreateProduct}
          />
        )}

        {showCSVModal && (
          <CSVUploadModal
            brandId={selectedBrand}
            sessionToken={sessionToken}
            onClose={() => setShowCSVModal(false)}
            onSuccess={() => { setShowCSVModal(false); loadProducts(); }}
          />
        )}
      </div>
    </Layout>
  );
};

const ProductModal = ({ brandId, initialData = null, onClose, onSave }) => {
  const [formData, setFormData] = useState(initialData || {
    brand_id: brandId,
    product_name: '',
    product_id_custom: '',
    picture_link: '',
    size: '',
    gender: 'male',
    price: '',
    category: 'shirt',
    custom_category: '',
    description: '',
    stock_quantity: 0
  });
  const [uploadedImage, setUploadedImage] = useState(initialData?.picture_link || null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Image = event.target.result;
        setUploadedImage(base64Image);
        setFormData({...formData, picture_link: base64Image});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.product_name || !formData.product_id_custom || !formData.picture_link || !formData.price) {
      alert('Please fill all required fields');
      return;
    }
    
    // Use custom category if "other" is selected
    const finalCategory = formData.category === 'other' ? formData.custom_category : formData.category;
    
    if (formData.category === 'other' && !formData.custom_category) {
      alert('Please specify custom category');
      return;
    }
    
    onSave({
      ...formData,
      category: finalCategory,
      price: parseFloat(formData.price) || 0,
      stock_quantity: parseInt(formData.stock_quantity) || 0
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{initialData ? '📋 Clone Product' : 'Add New Product'}</h2>
        {initialData && (
          <p className="clone-notice">
            ℹ️ Cloning from: {initialData.product_name}. Please change Product Name and SKU.
          </p>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Product Name *</label>
            <input
              type="text"
              value={formData.product_name}
              onChange={(e) => setFormData({...formData, product_name: e.target.value})}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>SKU/Product ID *</label>
              <input
                type="text"
                value={formData.product_id_custom}
                onChange={(e) => setFormData({...formData, product_id_custom: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Price *</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Product Image *</label>
            <div className="image-upload-section">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                id="product-image-upload"
                style={{display: 'none'}}
              />
              <label htmlFor="product-image-upload" className="upload-btn">
                📷 Upload Image
              </label>
              {uploadedImage && (
                <div className="image-preview">
                  <img src={uploadedImage} alt="Preview" />
                  <span className="preview-label">✓ Image uploaded</span>
                </div>
              )}
              {!uploadedImage && (
                <input
                  type="url"
                  value={formData.picture_link}
                  onChange={(e) => setFormData({...formData, picture_link: e.target.value})}
                  placeholder="Or paste image URL"
                  className="url-input"
                />
              )}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Size *</label>
              <input
                type="text"
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Gender *</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({...formData, gender: e.target.value})}
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="unisex">Unisex</option>
                <option value="kids">Kids</option>
              </select>
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                <option value="shirt">Shirt</option>
                <option value="pants">Pants</option>
                <option value="dress">Dress</option>
                <option value="shoes">Shoes</option>
                <option value="jacket">Jacket</option>
                <option value="other">Other (Specify)</option>
              </select>
            </div>
          </div>
          
          {formData.category === 'other' && (
            <div className="form-group">
              <label>Custom Category *</label>
              <input
                type="text"
                value={formData.custom_category}
                onChange={(e) => setFormData({...formData, custom_category: e.target.value})}
                placeholder="e.g., Accessories, Swimwear, etc."
                required
              />
            </div>
          )}
          
          <div className="form-group">
            <label>Stock Quantity</label>
            <input
              type="number"
              value={formData.stock_quantity}
              onChange={(e) => setFormData({...formData, stock_quantity: parseInt(e.target.value)})}
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
            />
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Add Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CSVUploadModal = ({ brandId, sessionToken, onClose, onSuccess }) => {
  const [csvData, setCsvData] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvData(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('brand_id', brandId);
      formData.append('csv_data', csvData);
      
      const response = await axios.post(
        `${API}/merchant/products/bulk`,
        formData,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.success) {
        alert(`Successfully uploaded ${response.data.products_created} products`);
        onSuccess();
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/merchant/products/csv-template`);
      const blob = new Blob([response.data.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_template.csv';
      a.click();
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>📊 CSV Bulk Upload</h2>
        <p>Upload multiple products at once using a CSV file</p>
        
        <button className="btn-secondary" onClick={downloadTemplate} style={{marginBottom: '1rem'}}>
          ⬇ Download Template
        </button>
        
        <div className="file-upload-area">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            id="csv-upload"
          />
          <label htmlFor="csv-upload">
            {csvData ? '✅ File Loaded' : '📄 Choose CSV File'}
          </label>
        </div>
        
        {csvData && (
          <div className="csv-preview">
            <pre>{csvData.split('\n').slice(0, 5).join('\n')}...</pre>
          </div>
        )}
        
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleUpload}
            disabled={!csvData || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Products'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductManagement;